import React from 'react';
import PropTypes from 'prop-types';
import LinkList from 'gatsby-core-theme/src/components/molecules/link-list';
import LazyImage from 'gatsby-core-theme/src/hooks/lazy-image';
import { imagePrettyUrl } from 'gatsby-core-theme/src/helpers/getters';
import Tnc from 'gatsby-core-theme/src/components/molecules/tnc';
import keygen from '~helpers/keygen';
import useTranslate from '~hooks/useTranslate/useTranslate';
import PrettyLink from '~atoms/pretty-link';
import OperatorCta from '~atoms/button/operator-cta';
import BonusBox from '~molecules/bonus-box/template-one';
import styles from './template-one.module.scss';
import ArrowIcon from '../../../../../../images/icons/arrow';
import DynamicNewsletter from '../../../../../organisms/dynamic-newsletter';
import { TrackingKeys } from '~constants/tracking-api';

const TemplateOne = ({
  page,
  moreImages = [
    {
      fileImage: '/images/headerItemOne.svg',
      width: 153,
      height: 138,
    },
    {
      fileImage: '/images/headerItemTwo.svg',
      width: 110,
      height: 116,
    },
  ],
  bonusIcon = {
    fileImage: '/images/headerItemThree.svg',
    width: 69,
    height: 74,
  },
  buttonIcon = <ArrowIcon />,
  simpleMode = false,
  moduleName = TrackingKeys?.HEADER.BONUS,
}) => {
  let BonusData = null;
  let newsletter = null;
  let headerMenu = null;
  page?.sections?.header?.modules?.forEach((element) => {
    if (element?.name === 'menu') {
      headerMenu = element;
    } else if (element?.name === 'bonus') {
      BonusData = element;
    } else if (element?.name === 'dynamic_newsletter') {
      newsletter = element;
    }
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const RatingText = useTranslate('rating_header', 'Ratings based on:');

  // Note: Page Extra fields
  const color = page?.extra_fields?.header_background_color;
  const bgImage = page?.extra_fields?.header_background_image;

  const iconone = page?.extra_fields?.header_item_one;
  const icontwo = page?.extra_fields?.header_item_two;
  const iconthree = page?.extra_fields?.header_item_three;

  if (iconone && icontwo) {
    moreImages = [
      {
        fileImage: imagePrettyUrl(iconone),
        width: 153,
        height: 138,
      },
      {
        fileImage: imagePrettyUrl(icontwo),
        width: 110,
        height: 116,
      },
    ];
  }

  if (iconthree) {
    bonusIcon = {
      fileImage: imagePrettyUrl(iconthree),
      width: 69,
      height: 74,
    };
  }

  const getBonus = (bonusModule) => {
    const operator = bonusModule.value;
    const itemName = operator?.name;
    // Note: Bonus Extra field
    const backgroundImage = operator?.extra_fields?.campaign_image;

    return (
      <div className={styles.bonus}>
        {!simpleMode && bonusIcon && bonusIcon?.fileImage && (
          <LazyImage
            alt="Bonus Icon Image"
            loading="eager"
            width={bonusIcon?.width}
            height={bonusIcon?.height}
            src={bonusIcon?.fileImage}
            className={styles?.bonusIcon}
          />
        )}
        <LazyImage
          alt="Desktop Bonus BG"
          loading="eager"
          width={387}
          height={387}
          src={
            backgroundImage
              ? imagePrettyUrl(`${backgroundImage.filename}${backgroundImage.extension}`, 387, 500)
              : '/images/desktopBonusBg.png'
          }
          className={styles.bunusBGDesktop}
        />
        <PrettyLink
          operator={operator}
          pageTemplate={page.template}
          moduleName={moduleName}
          tracker={bonusModule.tracking_link_name}
          clickedElement="operator_logo"
          className={styles.bonusOperatorlogoContainer}
        >
          <LazyImage
            alt={operator?.name}
            loading="eager"
            width={168}
            height={168}
            src={imagePrettyUrl(operator?.logo?.filename, 168, 168)}
            className={styles.bonusOperatorlogo}
          />
        </PrettyLink>

        <div className={styles.bonusInfo}>
          <PrettyLink
            operator={operator}
            pageTemplate={page.template}
            moduleName={moduleName}
            tracker={bonusModule.tracking_link_name}
            clickedElement="operator_name"
            className={styles.bonusOperatorName}
          >
            {itemName}
          </PrettyLink>

          <BonusBox
            item={operator}
            tracker={bonusModule.tracking_link_name}
            onlinerTracker={bonusModule.type}
            moduleName={moduleName}
            pageTemplate={page.template}
          />

          <OperatorCta
            operator={operator}
            tracker={bonusModule.tracking_link_name}
            icon={buttonIcon}
            moduleName={moduleName}
            pageTemplate={page?.template}
            gtmClass="toplist-operator-cta-gtm"
          />
          <div className={styles.tnc}>
            <Tnc isFixed operator={operator} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container} style={color ? { background: color } : {}}>
      {bgImage && (
        <LazyImage
          alt="bgImage"
          loading="eager"
          width={1400}
          height={632}
          src={imagePrettyUrl(bgImage, 1400, 632)}
          className={styles.headerBackground}
        />
      )}

      <div className={styles.headerContainer}>
        {newsletter ? (
          <>
            <h1 className={styles.pageTitle}>{page.title}</h1>
            <DynamicNewsletter page={page} />
          </>
        ) : (
          <>
            {!simpleMode && (
              <>
                <h1 className={styles.pageTitle}>{page.title}</h1>
                {headerMenu?.children?.length > 0 && (
                  <div className={styles.headerMenu}>
                    <span>{RatingText}</span>
                    <LinkList
                      showListTitle={false}
                      imageOnly
                      singleList
                      lists={headerMenu}
                      width={105}
                      height={20}
                      gtmClass="mobile-menu-gtm logo-cta"
                    />
                  </div>
                )}
              </>
            )}
            {BonusData && getBonus(BonusData)}
          </>
        )}
      </div>

      {!simpleMode &&
        moreImages &&
        moreImages?.length > 0 &&
        moreImages?.map((elm, index) => (
          <LazyImage
            key={keygen()}
            alt={`Header Image ${index + 1}`}
            loading="eager"
            width={elm?.width}
            height={elm?.height}
            src={elm?.fileImage}
            className={styles[`headerItem${index + 1}`]}
          />
        ))}
    </div>
  );
};

TemplateOne.propTypes = {
  page: PropTypes.string,
  moreImages: PropTypes.arrayOf(PropTypes.shape({})),
  buttonIcon: PropTypes.element,
  bonusIcon: PropTypes.shape({}),
  simpleMode: PropTypes.bool,
  moduleName: PropTypes.string,
};

export default TemplateOne;
